import React, { createContext, useContext, useState, useEffect } from 'react';
import { playSound, preloadAllSounds } from '../utils/soundUtils';

const SoundContext = createContext();

export const useSound = () => {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error('useSound must be used within a SoundProvider');
  }
  return context;
};

export const SoundProvider = ({ children }) => {
  const [soundEnabled, setSoundEnabled] = useState(() => {
    // Load sound preference from localStorage, default to true
    const saved = localStorage.getItem('mecharena-sound-enabled');
    return saved !== null ? saved === 'true' : true;
  });

  // Save preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('mecharena-sound-enabled', soundEnabled.toString());
  }, [soundEnabled]);

  // Preload sounds when component mounts and sound is enabled
  useEffect(() => {
    if (soundEnabled) {
      preloadAllSounds();
    }
  }, [soundEnabled]);

  // Sound functions
  const playButtonClick = () => {
    if (soundEnabled) {
      playSound('/sounds/button-click.mp3', 0.4);
    }
  };

  const playButtonHover = () => {
    if (soundEnabled) {
      playSound('/sounds/button-hover-sound.mp3', 0.3);
    }
  };

  const playMenuOptionHover = () => {
    if (soundEnabled) {
      playSound('/sounds/menu-option-hover.wav', 0.3);
    }
  };

  const playInterfaceSelectionHover = () => {
    if (soundEnabled) {
      playSound('/sounds/interface-selection-hover.wav', 0.3);
    }
  };

  const playMouseHover = () => {
    if (soundEnabled) {
      playSound('/sounds/mouse-hover.wav', 0.2);
    }
  };

  const playOptionHoverClick = () => {
    if (soundEnabled) {
      playSound('/sounds/option-hover-click.wav', 0.3);
    }
  };

  const playNavigationClick = () => {
    if (soundEnabled) {
      playSound('/sounds/navigation-click.wav', 0.4);
    }
  };

  const playSelectOptionOpen = () => {
    if (soundEnabled) {
      playSound('/sounds/select-option-open.wav', 0.3);
    }
  };

  const toggleSound = () => {
    setSoundEnabled(prev => !prev);
  };

  const value = {
    soundEnabled,
    toggleSound,
    playButtonClick,
    playButtonHover,
    playMenuOptionHover,
    playInterfaceSelectionHover,
    playMouseHover,
    playOptionHoverClick,
    playNavigationClick,
    playSelectOptionOpen
  };

  return (
    <SoundContext.Provider value={value}>
      {children}
    </SoundContext.Provider>
  );
};

